import {
  InteractionRequiredAuthError,
  type IPublicClientApplication,
  type SilentRequest,
} from '@azure/msal-browser';

/**
 * Robust access-token acquisition for the Readiness SPA.
 *
 * Implements the token-lifecycle rules of the SPA-Auth Standard
 * (`.github/guides/spa-auth-standard.md`):
 *  - Rule 2: `await instance.initialize()` before any token call (idempotent).
 *  - Rule 4: on interaction-required (including the hidden-iframe timeout
 *    cases), fall back to an interactive REDIRECT — never dead-end. Guarded by
 *    a `sessionStorage` flag so it fires at most once per session and clears
 *    on the next successful acquire (prevents a redirect loop).
 *
 * Returns the access token, or `null` when an interactive redirect is in
 * flight (the page is navigating away) or has already been attempted this
 * session. Genuine (non-interaction) errors are re-thrown for the caller to
 * handle — this helper never signs the user out.
 */

/** Session flag: the interactive-redirect fallback fires at most once per session. */
export const INTERACTIVE_ATTEMPT_KEY = 'tfmr.auth.interactiveAttempt';

/**
 * MSAL error codes that mean "silent acquisition cannot recover without
 * interaction". Includes the hidden-iframe timeout codes (`timed_out`,
 * `monitor_window_timeout`) — silent renewal in an iframe can hang/expire
 * under third-party-cookie blocking, and the recovery is the same: interactive.
 */
const INTERACTION_ERROR_CODES = new Set([
  'login_required',
  'interaction_required',
  'consent_required',
  'timed_out',
  'monitor_window_timeout',
]);

/** True when a silent failure requires interactive auth to recover. */
export function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  const code = (err as { errorCode?: string } | null)?.errorCode;
  return code !== undefined && INTERACTION_ERROR_CODES.has(code);
}

export async function acquireToken(
  instance: IPublicClientApplication,
  request: SilentRequest,
): Promise<string | null> {
  // Rule 2 — initialize() is idempotent; safe (and required) on every path.
  await Promise.resolve(instance.initialize());

  const account = request.account ?? instance.getActiveAccount() ?? undefined;
  if (!account) return null;

  try {
    const result = await instance.acquireTokenSilent({ ...request, account });
    // Success → clear the loop guard so a future expiry can redirect again.
    sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
    return result.accessToken;
  } catch (err) {
    if (needsInteraction(err)) {
      // Rule 4 — guarded interactive redirect, once per session.
      if (!sessionStorage.getItem(INTERACTIVE_ATTEMPT_KEY)) {
        sessionStorage.setItem(INTERACTIVE_ATTEMPT_KEY, '1');
        await instance.acquireTokenRedirect({
          scopes: request.scopes,
          account,
        });
      }
      // Redirect in flight (or already tried this session) — caller gets null.
      return null;
    }
    // Genuine error — let the caller decide; do NOT dead-end / sign out here.
    throw err;
  }
}
